import { startTransition, useState } from 'react';
import useSWR from 'swr';
import { useDeviceType, useI18n } from '@packages/shared';
import { useConfig } from '@packages/utilities';
import type { NavigationData } from './types';
import { getNavigationDataEndpoint } from './getNavigationDataEndpoint';
import { getNavigationDataCacheKey } from './getNavigationDataCacheKey';

const fetcher = (endpoint: string) => (): Promise<NavigationData> =>
  fetch(endpoint).then((res) => {
    if (!res.ok) {
      // throw error when response status not ok -> error boundary catches and logs the error
      throw new Error(`Endpoint: ${res.url} - Status ${res.status}: ${res.statusText}`);
    }
    return res.json();
  });

/**
 * Fetch navigation data from the API
 *
 * @returns navigation data, initially only the first few levels ("sliced"), and a function to load the full data on-demand
 */
export const useNavigationData = (): {
  data: NavigationData;
  loadFullData: () => void;
} => {
  const {
    clientId,
    navigation: { loadSliced },
  } = useConfig();

  const [sliced, setSliced] = useState(loadSliced);
  const { locale } = useI18n();
  const { isDesktop } = useDeviceType();

  // TODO: re-evaluate why a cache key separate from the endpoint is necessary, seems to serve no purpose
  const cacheKey = getNavigationDataCacheKey(clientId, isDesktop ? 'web' : 'mob', locale, sliced);
  const endpoint = getNavigationDataEndpoint(clientId, isDesktop ? 'web' : 'mob', locale, sliced);

  const { data } = useSWR<NavigationData>(cacheKey, fetcher(endpoint), {
    keepPreviousData: true,
  });

  const loadFullData = () => {
    startTransition(() => setSliced(false));
  };

  return { data: data || [], loadFullData };
};
